import { observable, computed, runInAction, makeObservable, action } from "mobx";
import _ from "lodash";
import { AppStore } from "~/stores/AppStore";
import { Modal } from "antd";
import { getAvailablePermissions } from "./permissionsData";

const { confirm } = Modal;

export class PermissionsController {
  public store: AppStore;
  public readOnly = true;
  public jsonViewer = null;

  // Structure for the available pages
  public permissionTypes = [
    {
      name: "General Permissions",
      api: "permissions/user-roles",
      allListApi: "permissions",
      apiType: "main",
      key: "general",
    },
  ];

  // Observable values
  public selectedPermissionType = this.permissionTypes[0];
  public userRoles = [];
  public selectedUserRole = null;
  public selectedOriginalUserRole = null;
  public fetchingRoles = false;
  public resyncModeEnabled = false;
  public showBulkEditModal = false;
  public sortPermissions = {
    sortBy: "",
    direction: "",
  };
  public permissionCloning = {
    visible: false,
    cloningFrom: null,
  };
  public tableSearch = "";

  constructor(store) {
    makeObservable(this, {
      fetchingRoles: observable,
      selectedPermissionType: observable,
      userRoles: observable,
      selectedUserRole: observable,
      selectedOriginalUserRole: observable,
      resyncModeEnabled: observable,
      showBulkEditModal: observable,
      sortPermissions: observable,
      permissionCloning: observable,
      tableSearch: observable,
      userRoutesData: computed,
      userRolesData: computed,
      currentUsersRole: computed,
      currentPermissionData: computed,
      setFetchingRoles: action,
      setSelectedPermissionType: action,
      setUsersRoles: action,
      setSelectedUserRole: action,
      setSelectedOriginalUserRole: action,
      setResyncModeEnabled: action,
      setSingleUserRole: action,
    });

    this.store = store;
    this.readOnly = this.store.isRouteReadOnly(window.location.pathname);
    this.loadUserRolesData();
  }

  get sortedPermissions() {
    if (this.sortPermissions.sortBy) {
      return _.orderBy(this.selectedUserRole.permissions, this.sortPermissions.sortBy, [this.sortPermissions.direction as "asc" | "desc"]);
    }

    return this.selectedUserRole.permissions;
  }

  get userRoutesData() {
    const searchVal = this.tableSearch.trim().toLowerCase();

    if (this.selectedUserRole) {
      return this.sortedPermissions.filter((p) => {
        if (searchVal) {
          return p.name.toLowerCase().includes(searchVal) || p.ref.toLowerCase().includes(searchVal);
        }

        return true;
      });
    }

    return [];
  }

  get userRolesData() {
    return _.orderBy(this.userRoles, "name", "asc");
  }

  get currentUsersRole() {
    return _.get(this.store.user, "roles", []).length > 0 ? _.get(this.store.user, "roles[0]", "") : "";
  }

  get currentPermissionData() {
    switch (this.selectedPermissionType.key) {
      case "general":
        return getAvailablePermissions();
      // case "route":
      //   return getRoutingPermissions();
      // case "dispatch":
      //   return getDispatchPermissions();
      default:
        return [];
    }
  }

  setFetchingRoles = (value) => {
    this.fetchingRoles = value;
  };

  setSelectedPermissionType = (value: any, key: string = null) => {
    if (key) {
      this.selectedPermissionType[key] = { ...value };
    } else {
      this.selectedPermissionType = value;
    }
  };

  setUsersRoles = (value) => {
    this.userRoles = value;
  };

  setSingleUserRole = (index, value) => {
    this.userRoles[index] = value;
  };

  setSelectedUserRole = (value) => {
    this.selectedUserRole = value;
  };

  setSelectedOriginalUserRole = (value) => {
    this.selectedOriginalUserRole = value;
  };

  setResyncModeEnabled = (value) => {
    this.resyncModeEnabled = value;
  };

  setShowBulkEditModal = (value) => {
    this.showBulkEditModal = value;
  };

  setSortPermissions = (value) => {
    this.sortPermissions = value;
  };

  setPermissionCloning = (value: any, key: string = null) => {
    if (key) {
      this.permissionCloning[key] = { ...value };
    } else {
      this.permissionCloning = value;
    }
  };

  setTableSearch = (value) => {
    this.tableSearch = value;
  };

  public validation = (type) => {
    switch (type) {
      case "equal":
        if (!_.isEqual(this.selectedUserRole, this.selectedOriginalUserRole)) {
          return true;
        }
        break;
      case "page":
        if (!this.fetchingRoles) {
          return true;
        }
        break;
      case "permission":
        if (this.selectedUserRole) {
          return true;
        }
        break;
      default:
        return false;
    }
  };

  public loadUserRolesData = async () => {
    const requests = [];
    let rolesList = [];
    this.setFetchingRoles(true);

    requests.push(
      this.store
        .getData(this.selectedPermissionType.api, this.selectedPermissionType.apiType)
        .then((response) => {
          this.setUsersRoles(response);

          this.prepareUserRolesData();
        })
        .catch((err) => {
          this.store.displayNotification(err.message, "error", true, `Failed to load user roles for ${this.selectedPermissionType.name}`);
        })
    );

    // requests.push(
    //   this.store
    //     .getData(this.selectedPermissionType.allListApi, this.selectedPermissionType.apiType)
    //     .then((response) => {
    //       rolesList = response;
    //     })
    //     .catch((err) => {
    //       this.store.displayNotification(err.message, "error", true, `Failed to load user roles list for ${this.selectedPermissionType.name}`);
    //     })
    // );

    Promise.all(requests).then(() => {
      //this.checkForDuplicateRefs(rolesList);

      this.setFetchingRoles(false);
    });
  };

  /**
   * This func used to align the stale data.
   * Might not be used in the future
   */
  public resyncRolesData = async (role = null) => {
    this.resetRoleSelection();

    // If the role specified, we resync only that role
    if (role) {
      this.saveRolePermissions(role);
    } else {
      this.setFetchingRoles(true);

      // Else we resync all the ones which has mismatched data
      const requests = [];
      let failed = false;
      let needsUserUpdate = false;

      this.userRolesData.forEach((ur) => {
        if (ur.mismatchedData) {
          requests.push(
            this.store
              .postData(this.selectedPermissionType.api + "/" + ur._id, this.prepareRolesPayload(ur), this.selectedPermissionType.apiType)
              .then((response) => {
                this.updateRole(response);

                if (this.currentUsersRole === ur.roleRef) {
                  needsUserUpdate = true;
                }
              })
              .catch(() => {
                failed = true;
              })
          );
        }
      });

      Promise.all(requests).then(() => {
        if (failed) {
          this.store.displayNotification("Some roles were not synched", "error", true, "Please sync them manually or all again.");
        } else {
          this.store.displayNotification("All User Roles Has Been Synced", "success", true, "All user roles have been synced successfully.");
        }

        // // If the current users role was updated - update user settings
        // if (needsUserUpdate) {
        //   this.store.updateUserInfo()
        // }

        this.setFetchingRoles(false);
      });
    }
  };

  public saveRolePermissions = async (role) => {
    this.setFetchingRoles(true);

    await this.store
      .postData(this.selectedPermissionType.api + "/" + role.roleRef, this.prepareRolesPayload(role), this.selectedPermissionType.apiType)
      .then((response) => {
        this.updateRole(response);

        // // If the saved role is the one which user is assigned to - refresh user settings
        // if (this.currentUsersRole === response.roleRef) {
        //   this.store.updateUserInfo();
        // }

        this.store.displayNotification(`Permission for ${role.name} Role Updated Successfully`, "success", true);
      })
      .catch((err) => {
        this.store.displayNotification(err.message, "error", true);
      });

    this.setFetchingRoles(false);
  };

  private prepareUserRolesData = () => {
    this.userRoles.forEach((role) => {
      role.permissions = this.preparePermissionsData(role);
    });
  };

  /**
   * Used to update the roles routes within 2 separate api collections
   * Might not be used in the future once the apis are not stale
   */
  private preparePermissionsData = (role) => {
    // Check if user role was removed from the constructor to notify that the role is not synched
    if (this.currentPermissionData.length !== _.get(role, "permissions", []).length) {
      role.mismatchedData = true;
    }

    const permissions = _.cloneDeep(this.currentPermissionData).map((permission: any) => {
      const rolesPermission = _.get(role, "permissions", []).find((p) => p.ref === permission.ref);

      //console.log("rolesPermission", permission.ref);

      if (rolesPermission) {
        //permission.past = rolesPermission.past;
        permission.current = rolesPermission.current;
        //permission.future = rolesPermission.future;
      } else {
        // if the route is not found from the roles, add the flag to indicate that the data is not synched with the BE
        permission.isNew = true;
        role.mismatchedData = true;
      }

      return permission;
    });

    return permissions;
  };

  // Remove flags which were generated from UI and no need to pass to BE
  private prepareRolesPayload = (role) => {
    // Update all user roles
    role.permissions.forEach((p) => {
      delete p.isNew;
      delete p.isUpdated;
      delete p.notes;
    });

    const payload = {
      name: role.name,
      roleRef: role.roleRef,
      permissions: role.permissions,
      _id: role._id,
    };

    return payload;
  };

  public exportRolePermissions = (userRoles: Array<any>, name: string, all = false) => {
    let permissions = [];

    // Copy user roles so we dont modify original values them when adding role name
    const roles = _.cloneDeep(userRoles);

    const fields = [
      {
        label: "Name",
        value: (row) => row.name,
        default: "",
      },
      {
        label: "Ref",
        value: (row) => row.ref,
        default: "",
      },
      {
        label: "Past",
        value: (row) => row.past,
        default: "",
      },
      {
        label: "Current",
        value: (row) => row.current,
        default: "",
      },
      {
        label: "Future",
        value: (row) => row.future,
        default: "",
      },
    ];

    // If exporting all permissions, add additional field for the user roles
    if (all) {
      fields.unshift({
        label: "User Role",
        value: (row) => row.userRole,
        default: "",
      });
    }

    roles.forEach((role) => {
      role.permissions.forEach((p) => {
        if (all) {
          p["userRole"] = role.name;
        }

        permissions.push(p);
      });
    });

    //chooseDelimiter(permissions, name, fields);
  };

  public handleUserRoleSelection = (userRoleId) => {
    // // Check if there are any unsaved edited values
    // if (this.validation("equal")) {
    //   confirm({
    //     title: "Pending unsaved changes",
    //     content: "Are you sure you want to discard all unsaved changes?",
    //     okText: "Yes",
    //     okType: "danger",
    //     cancelText: "Cancel",
    //     centered: true,
    //     onOk: () => {
    //       this.resetRoleSelection();
    //       this.handleUserRoleSelection(userRoleId);
    //     },
    //   });
    //   return;
    // }

    const selectedRole = this.userRolesData.find((ur) => ur._id === userRoleId);

    console.log(selectedRole);

    this.selectedUserRole = _.cloneDeep(selectedRole);
    this.selectedOriginalUserRole = _.cloneDeep(selectedRole);

    this.resetSearchValues();
  };

  public resyncRoles = (role = null) => {
    let desc = "Are you sure you want to resync all the roles data?";

    if (role) {
      desc = `Are you sure you want to resync the data of the ${role.name} role?`;
    }

    confirm({
      title: "Resync Roles Data",
      content: desc,
      okText: "Yes",
      okType: "primary",
      cancelText: "Cancel",
      centered: true,
      onOk: () => this.resyncRolesData(role),
    });
  };

  public updateRole = (role, type: "update" | "create" | "delete" = "update") => {
    const roleIndex = this.userRoles.findIndex((ur) => ur.roleRef === role.roleRef);

    if (roleIndex > -1) {
      console.log("UPDATE ROLE", role);
      console.log("UPDATE ROLE OLD", this.userRoles[roleIndex]);

      this.setSingleUserRole(roleIndex, role);
    }

    // switch (type) {
    //   case "create":
    //     this.userRoles.push(role);
    //     break;

    //   case "delete":
    //     if (~roleIndex) {
    //       this.userRoles.splice(roleIndex, 1);
    //     }

    //     break;
    //   default:
    //     if (~roleIndex) {
    //       this.userRoles[roleIndex] = role;
    //     }

    //     break;
    // }

    //this.resetRoleSelection();
  };

  public resetSearchValues = () => {
    this.tableSearch = "";
  };

  public resetRoleSelection = () => {
    this.setSelectedUserRole(null);
    this.setSelectedOriginalUserRole(null);
  };

  public enableResyncMode = () => {
    this.resyncModeEnabled = true;
  };

  public cloneRolesPermissions = (role) => {
    role.permissions = _.cloneDeep(this.permissionCloning.cloningFrom.permissions);

    this.permissionCloning.visible = false;

    this.handleUserRoleSelection(role._id);
  };

  public bulkEditRolePermissions = (selectedRoles, selectedPermissions, setPermissions) => {
    this.showBulkEditModal = false;
    this.setFetchingRoles(true);

    const requests = [];
    let failed = [];
    let needsUserUpdate = false;

    selectedRoles.forEach((role) => {
      role.permissions.forEach((permission) => {
        if (selectedPermissions.some((sp) => sp._id === permission._id)) {
          permission.past = setPermissions.past;
          permission.current = setPermissions.current;
          permission.future = setPermissions.future;
        }
      });

      requests.push(
        this.store
          .postData(this.selectedPermissionType.api + "/" + role._id, this.prepareRolesPayload(role), this.selectedPermissionType.apiType)
          .then((response) => {
            this.updateRole(response);

            if (this.currentUsersRole === role.roleRef) {
              needsUserUpdate = true;
            }
          })
          .catch(() => {
            failed.push(role.name);
          })
      );
    });

    Promise.all(requests).then(() => {
      if (failed.length > 0) {
        this.store.displayNotification("Some Selected Roles Failed To Update", "error", true, `Roles ${failed.join(", ")} failed to update`);
      } else {
        this.store.displayNotification("All User Roles Has Been Updated", "success", true, "All selected user roles have been updated successfully.");
      }

      // // If the current users role was updated - update user settings
      // if (needsUserUpdate) {
      //   this.store.updateUserInfo();
      // }

      this.setFetchingRoles(false);
    });
  };

  public checkForDuplicateRefs = (rolesList) => {
    let duplicates = [];

    this.userRoles.forEach((ur) => {
      const filteredRoles = rolesList.filter((role) => role.roleRef === ur.roleRef);

      if (filteredRoles.length > 1) {
        duplicates.push(ur.roleRef);
      }
    });

    if (duplicates.length > 0) {
      Modal.warning({
        centered: true,
        title: "Duplicate Role Refs Detected",
        content: `Following user role refs were duplicated: ${duplicates.join(", ")}. Please contact support.`,
      });
    }
  };
}
